import _ from 'lodash'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label
} from 'recharts'
import ReactResizeDetector from 'react-resize-detector'
import styles from './ViewsChart.css'
import { numeralFormat } from '../../../../../../../lib/renderUtil'
import {
  TIME_DURATION, DATE_FORMAT
} from '../../../../../../lib/MomentHelper'
import RadioGroup from '../../../../../Common/NewDashboard/RadioGroup/RadioGroup'
import ComposeChartModule from '../../../../../Common/NewDashboard/Chart/BaseChartModule/ComposeChart/ComposeChart'
import LineDot from '../../../../../Common/NewDashboard/Chart/LineDot/LineDot'
import YAxisTick from '../../../../../Common/NewDashboard/Chart/YAxisTick/YAxisTick'
import YAxisLabel from '../../../../../Common/NewDashboard/Chart/YAxisLabel/YAxisLabel'
import CustomizeXAxisTick from './CustomizeXAxisTick/CustomizeXAxisTick'
import CustomizedLegend from './CustomizedLegend/CustomizedLegend'
import CustomizedTooltip from './CustomizedTooltip/CustomizedTooltip'
import { CARRIER } from '../../../constants/carrier'
import {
  FIELD as SEARCH_FIELD
} from '../SearchForm/SearchForm'

export const COMPARISON_PREFIX = `${SEARCH_FIELD.COMPARISON_RANGE}-`

export const FIELD = {
  REPORT_DATE: 'reportDate',
  PAGE_VIEW: 'pageView',
  VIEW_TIME: 'viewTime'
}

export const PRIMARY_COLOR = {
  [CARRIER.ALL]: '#f6c02d',
  [CARRIER.APP]: '#5b8ff9',
  [CARRIER.MOBILE_WEB]: '#e96c5c',
  [CARRIER.DESKTOP_WEB]: '#657798',
  [CARRIER.UNKNOWN]: '#74cbed'
}

export const SECONDARY_COLOR = {
  [CARRIER.ALL]: '#fcecbd',
  [CARRIER.APP]: '#cfdffd',
  [CARRIER.MOBILE_WEB]: '#f8d2ce',
  [CARRIER.DESKTOP_WEB]: '#d0d6e0',
  [CARRIER.UNKNOWN]: '#d6eff9'
}

export const TOOLTIP_DETAIL = {
  ORDER_AMOUNT: 'newOrderAmount',
  ORDER_QUANTITY: 'newOrderQuantity',
  REFUND_AMOUNT: 'refundAmount',
  REFUND_QUANTITY: 'refundQuantity'
}

export const SELLER_TOOLTIP_DETAIL = {
  ...TOOLTIP_DETAIL,
  SOLD_QUANTITY: 'soldQuantity',
  CONVERSION_RATE: 'conversionRate',
  UNIQUE_CART_QUANTITY: 'uniqueCartQuantity',
  VIEWER_QUANTITY: 'viewerQuantity',
  ADD_CART_USER_QUANTITY: 'addCartUserQuantity',
  VIEW_TIME: 'viewTime'
}

export const TOOLTIP_DETAIL_LIST = [
  TOOLTIP_DETAIL.ORDER_AMOUNT,
  TOOLTIP_DETAIL.ORDER_QUANTITY,
  TOOLTIP_DETAIL.REFUND_AMOUNT,
  TOOLTIP_DETAIL.REFUND_QUANTITY
]

export const SELLER_TOOLTIP_DETAIL_LIST = [
  // remove REFUND_AMOUNT and REFUND_QUANTITY
  ..._.without(TOOLTIP_DETAIL_LIST, TOOLTIP_DETAIL.REFUND_AMOUNT, TOOLTIP_DETAIL.REFUND_QUANTITY),
  SELLER_TOOLTIP_DETAIL.SOLD_QUANTITY,
  SELLER_TOOLTIP_DETAIL.CONVERSION_RATE,
  SELLER_TOOLTIP_DETAIL.VIEWER_QUANTITY,
  SELLER_TOOLTIP_DETAIL.UNIQUE_CART_QUANTITY,
  SELLER_TOOLTIP_DETAIL.ADD_CART_USER_QUANTITY,
  SELLER_TOOLTIP_DETAIL.VIEW_TIME
]

// 購中店只選一個裝置時，也要顯示的 tooltip 欄位 [APACSMDESK-79619]
export const SELLER_TOOLTIP_DETAIL_WITH_ONE_CARRIER_LIST = [
  SELLER_TOOLTIP_DETAIL.VIEWER_QUANTITY,
  SELLER_TOOLTIP_DETAIL.UNIQUE_CART_QUANTITY,
  SELLER_TOOLTIP_DETAIL.ADD_CART_USER_QUANTITY,
  SELLER_TOOLTIP_DETAIL.VIEW_TIME
]

export const TOOLTIP_DETAIL_MAP = {
  [TOOLTIP_DETAIL.ORDER_AMOUNT]: '訂單成立金額',
  [TOOLTIP_DETAIL.ORDER_QUANTITY]: '訂單成立數量',
  [TOOLTIP_DETAIL.REFUND_AMOUNT]: '退貨金額',
  [TOOLTIP_DETAIL.REFUND_QUANTITY]: '退貨數量'
}

export const SELLER_TOOLTIP_DETAIL_MAP = {
  ...TOOLTIP_DETAIL_MAP,
  [SELLER_TOOLTIP_DETAIL.SOLD_QUANTITY]: '訂購量',
  [SELLER_TOOLTIP_DETAIL.CONVERSION_RATE]: '轉換率',
  [SELLER_TOOLTIP_DETAIL.VIEWER_QUANTITY]: '不重複訪客數',
  [SELLER_TOOLTIP_DETAIL.UNIQUE_CART_QUANTITY]: '不重複購物車數',
  [SELLER_TOOLTIP_DETAIL.ADD_CART_USER_QUANTITY]: '加入購物車次數',
  [SELLER_TOOLTIP_DETAIL.VIEW_TIME]: '停留時間(秒)'
}

const TIME_DURATION_OPTIONS = [
  TIME_DURATION.DAY,
  TIME_DURATION.WEEK,
  TIME_DURATION.MONTH
]

const COMPARISON_PREFIX_REGEXP = new RegExp(`^${COMPARISON_PREFIX}`)

const calcXaxisInterval = (mainData, comparisonData) => {
  const dataSizes = _.reduce([...mainData, ...comparisonData], (sizes, item) => {
    sizes.push(_.size(item.data))
    return sizes
  }, [])
  const xaxisInterval = _.round(_.max(dataSizes) / 15) - 1
  return xaxisInterval < 0 ? 0 : xaxisInterval
}

export const getGroupByReportDateTypePayload = (payload, key) => {
  const sortedPayload = _.flow(
    payload => _.groupBy(payload, item => {
      const isComparison = COMPARISON_PREFIX_REGEXP.test(item[key])
      if (isComparison) {
        return SEARCH_FIELD.COMPARISON_RANGE
      }
      return SEARCH_FIELD.MAIN_RANGE
    }),
    groupedPayload => {
      if (!(SEARCH_FIELD.COMPARISON_RANGE in groupedPayload)) {
        return groupedPayload
      }
      const transformedComparisonPayload = _.flow(
        groupedPayload => _.get(groupedPayload, SEARCH_FIELD.COMPARISON_RANGE, []),
        comparisonPayload => _.map(comparisonPayload, item => {
          return {
            ...item,
            [key]: _.replace(item[key], COMPARISON_PREFIX_REGEXP, '')
          }
        })
      )(groupedPayload)
      _.set(groupedPayload, SEARCH_FIELD.COMPARISON_RANGE, transformedComparisonPayload)
      return groupedPayload
    }
  )(payload)
  return sortedPayload
}

class ViewsChart extends ComposeChartModule {
  constructor (props) {
    super(props)
    this.state = {}
  }

  render () {
    const {
      timeDuration,
      summaries,
      data: [mainData = [], comparisonData = []],
      onTimeDurationChange,
      isDailyDetailLoaded,
      saleStateMapByTimeDuration,
      isDetailVisible,
      isSeller,
      isOneCarrierSelected,
      isAllCarrierSelected
    } = this.props
    const reportDateFormat = (timeDuration === TIME_DURATION.MONTH.value)
      ? DATE_FORMAT.LINE_CHART_X_AXIS_MONTH
      : DATE_FORMAT.LINE_CHART_X_AXIS_DATE
    const xaxisInterval = calcXaxisInterval(mainData, comparisonData)
    return (
      <>
        <div className={styles.groupBtnsArea}>
          <RadioGroup
            value={timeDuration}
            onChange={onTimeDurationChange}
            options={TIME_DURATION_OPTIONS}
          />
        </div>
        <ReactResizeDetector
          handleWidth
          handleHeight
          onResize={this.updateChartWidth}
        />
        <ResponsiveContainer
          width='100%'
          height='100%'
          minHeight='330px'
        >
          {/* 要調整 tooltip cursor 寬度一定要用 ComposedChart，用 LineChart 無法設定寬度 */}
          <ComposedChart
            ref={this.initChart}
            margin={{
              top: -40,
              left: 10,
              bottom: 5,
              right: 20
            }}
          >
            <XAxis
              tickLine={false}
              dataKey={FIELD.REPORT_DATE}
              tick={<CustomizeXAxisTick format={reportDateFormat} />}
              padding={{ left: 10 }}
              interval={xaxisInterval}
              type='category'
              allowDuplicatedCategory={false}
            />
            <YAxis
              dataKey={FIELD.PAGE_VIEW}
              axisLine={false}
              tickLine={false}
              tick={(
                <YAxisTick
                  customizeTickValue={value => numeralFormat(value, { allowZero: true })}
                  textAnchor='end'
                />
              )}
            >
              <Label
                value='瀏覽量'
                offset={20}
                position='left'
                angle={-90}
                content={<YAxisLabel />}
              />
            </YAxis>
            <CartesianGrid
              vertical={false}
              stroke='#f5f5f5'
            />
            <Tooltip
              cursor={{
                stroke: '#eee',
                strokeWidth: this.state.tooltipCursorWidth
              }}
              content={(
                <CustomizedTooltip
                  format={reportDateFormat}
                  timeDuration={timeDuration}
                  saleStateMapByTimeDuration={saleStateMapByTimeDuration}
                  isDailyDetailLoaded={isDailyDetailLoaded}
                  isDetailVisible={isDetailVisible}
                  isSeller={isSeller}
                  isOneCarrierSelected={isOneCarrierSelected}
                  isAllCarrierSelected={isAllCarrierSelected}
                />
              )}
            />
            <Legend
              align='left'
              verticalAlign='top'
              wrapperStyle={{
                top: '-40px',
                color: '#9aa0ab'
              }}
              content={<CustomizedLegend summaries={summaries} />}
            />
            {mainData.map(s => (
              <Line
                dataKey={FIELD.PAGE_VIEW}
                data={s.data}
                name={s.name}
                key={s.name}
                stroke={PRIMARY_COLOR[s.carrier]}
                strokeWidth={2}
                activeDot={false}
                dot={<LineDot />}
                // https://github.com/recharts/recharts/issues/804
                isAnimationActive={false}
              />
            ))}
            {comparisonData.map(s => (
              <Line
                dataKey={FIELD.PAGE_VIEW}
                data={s.data}
                name={s.name}
                key={s.name}
                stroke={SECONDARY_COLOR[s.carrier]}
                strokeWidth={2}
                activeDot={false}
                dot={<LineDot />}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </>
    )
  }
}

export default ViewsChart
